import { CostModel, CostModelTotalsLite } from "../interfaces/CostModel";
import { fetchDataStrict, writeData } from "../clients/api";
import { ForbiddenError, handleError } from "../errors";
import { Result } from "./types";
import { CacheHandle, clearTogether, defineCachedResource } from "./helpers";

type CostModelScope = { projectId: string; costModelId: string };

const TABLE = "ediphi_cost_models";

/**
 * Uncached list fetch — the probe path must hit the server fresh to detect changes.
 *
 * The search column is deliberately unqualified: the qualified `ediphi_cost_models.project` form
 * takes a branch server-side that rejects a user with no explicit permission row outright, where
 * this one narrows the allowed-project filter and returns an empty list instead. The project is
 * re-checked here because that filter, not the search term, is what the server enforces.
 */
export const fetchCostModelsByProjectIdUncached = async (projectId: string): Promise<Result<CostModel[]>> => {
  try {
    const response = await fetchDataStrict({
      table: TABLE,
      params: { search: [`project;${projectId}`] },
    });
    const costModels: CostModel[] = response?.data || [];
    return { success: true, data: costModels.filter((costModel) => costModel.project === projectId) };
  } catch (error) {
    const err = handleError(error, { table: TABLE, context: "fetchCostModelsByProjectIdUncached" });
    return { success: false, error: err };
  }
};

// Resolved out of the project list because the single-model route requires a permission the list
// does not, so a lookup there would 403 users who can see the model.
const fetchCostModelFromProjectUncached = async ({
  projectId,
  costModelId,
}: CostModelScope): Promise<Result<CostModel>> => {
  const result = await fetchCostModelsByProjectIdUncached(projectId);
  if (!result.success) return result;
  return { success: true, data: (result.data ?? []).find((costModel) => costModel.id === costModelId) };
};

const selectedCostModel = defineCachedResource<CostModelScope, CostModel>({
  scopeKey: ({ costModelId }) => costModelId,
  fetch: fetchCostModelFromProjectUncached,
});

export const fetchSelectedCostModel = (projectId: string, costModelId: string): Promise<Result<CostModel>> =>
  selectedCostModel.get({ projectId, costModelId });

const fetchCostModelTotalsUncached = async (costModelId: string): Promise<Result<CostModelTotalsLite>> => {
  try {
    const response = await writeData({
      table: TABLE,
      subRoute: "batch/totals",
      payload: { costModelIds: [costModelId] },
    });
    return { success: true, data: response?.data?.[costModelId] };
  } catch (error) {
    const err = handleError(error, { table: TABLE, subRoute: "batch/totals", context: "fetchCostModelTotalsUncached" });
    // Cost model access is granted separately from project access, so a 403 here is a routine state
    // the user can resolve — it earns its own wording rather than the generic load failure.
    if (err instanceof ForbiddenError) {
      return {
        success: false,
        error: new ForbiddenError(
          "You don't have permission to open this cost model. Ask a project owner for cost model access."
        ),
      };
    }
    return { success: false, error: err };
  }
};

const costModelTotals = defineCachedResource<string, CostModelTotalsLite>({
  scopeKey: (costModelId) => costModelId,
  fetch: fetchCostModelTotalsUncached,
});

/** Headline totals for a single model — the cheap endpoint, for callers that need no grid. */
export const fetchCostModelTotals = costModelTotals.get;

/**
 * Fetch the selected cost model and its totals fresh, and save both only when both succeed, so a
 * failed refresh leaves the caches as they were (see refreshGCGRCaches). `costModel` succeeding with
 * no data means the project no longer has it, and `totals` is then null.
 */
export const refreshCostModelCaches = async (
  projectId: string,
  costModelId: string
): Promise<{ costModel: Result<CostModel>; totals: Result<CostModelTotalsLite> | null }> => {
  const notCleared = selectedCostModel.watchForClear();
  const costModel = await fetchCostModelFromProjectUncached({ projectId, costModelId });
  if (!costModel.success || !costModel.data) return { costModel, totals: null };
  const totals = await fetchCostModelTotalsUncached(costModelId);
  if (totals.success && totals.data) {
    costModelTotals.stage(costModelId, totals.data, notCleared);
    selectedCostModel.stage({ projectId, costModelId }, costModel.data, notCleared);
  }
  return { costModel, totals };
};

export const costModelCaches: CacheHandle[] = [selectedCostModel, costModelTotals];

export const clearCostModelCaches = (): void => clearTogether(costModelCaches);
